/**
 * Player progression store for Build the Stack
 *
 * Manages level progress, achievements, portfolio content unlocking,
 * and player statistics using Zustand with persistence.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  Achievement,
  AchievementProgress,
  GameLevel,
  LevelProgress,
  PlayerProgression,
  TileType,
} from '../types'

/**
 * Progression store actions interface
 */
interface ProgressionActions {
  // Level progress management
  /**
   * Update progress for a specific level
   */
  updateLevelProgress: (
    levelId: string,
    progress: Partial<LevelProgress>
  ) => void

  /**
   * Mark a level as completed with grade and score
   */
  completeLevel: (
    levelId: string,
    grade: 'A' | 'B' | 'C' | 'D' | 'F',
    score: number,
    completionTime: number
  ) => void

  /**
   * Get progress for a specific level
   */
  getLevelProgress: (levelId: string) => LevelProgress | null

  /**
   * Check if a level is unlocked
   */
  isLevelUnlocked: (levelId: string, allLevels: GameLevel[]) => boolean

  // Achievement management
  /**
   * Update progress for a specific achievement
   */
  updateAchievementProgress: (
    achievementId: string,
    progress: Partial<AchievementProgress>
  ) => void

  /**
   * Unlock an achievement
   */
  unlockAchievement: (achievementId: string) => void

  /**
   * Check achievement requirements and auto-unlock
   */
  checkAchievements: (achievements: Achievement[]) => string[]

  /**
   * Mark achievement content as viewed
   */
  markContentViewed: (achievementId: string) => void

  // Portfolio content management
  /**
   * Unlock portfolio content
   */
  unlockContent: (contentId: string) => void

  /**
   * Check if content is unlocked
   */
  isContentUnlocked: (contentId: string) => boolean

  // Statistics management
  /**
   * Update player statistics
   */
  updateStatistics: (stats: Partial<PlayerProgression['statistics']>) => void

  /**
   * Increment tiles placed counter
   */
  incrementTilesPlaced: (tileType: TileType) => void

  /**
   * Increment connections made counter
   */
  incrementConnections: () => void

  /**
   * Update playtime
   */
  updatePlaytime: (sessionTime: number) => void

  // Preferences management
  /**
   * Update player preferences
   */
  updatePreferences: (
    preferences: Partial<PlayerProgression['preferences']>
  ) => void

  // Progress calculations
  /**
   * Calculate overall completion percentage
   */
  calculateCompletionPercentage: (allLevels: GameLevel[]) => number

  /**
   * Get current level recommendation
   */
  getCurrentLevelRecommendation: (allLevels: GameLevel[]) => string | null

  /**
   * Reset all progression data
   */
  resetProgression: () => void
}

/**
 * Combined progression store interface
 */
interface ProgressionStore extends PlayerProgression, ProgressionActions {}

/**
 * Default player progression state
 */
const defaultPlayerProgression: PlayerProgression = {
  currentLevel: 'tutorial-01',
  levelsCompleted: 0,
  achievementsUnlocked: 0,
  completionPercentage: 0,
  totalPlaytime: 0,
  levelProgress: new Map(),
  achievementProgress: new Map(),
  unlockedContent: [],
  preferences: {
    skipIntros: false,
    showAdvancedMetrics: false,
    preferredDifficulty: 'tutorial',
    enableHints: true,
  },
  statistics: {
    totalTilesPlaced: 0,
    totalConnections: 0,
    totalRequests: 0,
    bestSuccessRate: 0,
    favoriteTileType: 'compute',
  },
}

/**
 * Player progression store implementation using Zustand with persistence
 */
export const useProgressionStore = create<ProgressionStore>()(
  persist(
    (set, get) => ({
      ...defaultPlayerProgression,

      // Level progress management actions
      updateLevelProgress: (
        levelId: string,
        progress: Partial<LevelProgress>
      ) => {
        const state = get()
        const currentProgress = state.levelProgress.get(levelId) || {
          levelId,
          completed: false,
          bestScore: 0,
          attempts: 0,
          allAchievementsUnlocked: false,
        }

        const updatedProgress = { ...currentProgress, ...progress }

        set(state => ({
          levelProgress: new Map(state.levelProgress).set(
            levelId,
            updatedProgress
          ),
        }))
      },

      completeLevel: (
        levelId: string,
        grade: 'A' | 'B' | 'C' | 'D' | 'F',
        score: number,
        completionTime: number
      ) => {
        const state = get()
        const currentProgress = state.levelProgress.get(levelId)
        const now = Date.now()

        const wasCompleted = currentProgress?.completed || false
        const newBestScore = Math.max(currentProgress?.bestScore || 0, score)
        const newBestTime = currentProgress?.bestTime
          ? Math.min(currentProgress.bestTime, completionTime)
          : completionTime

        // Update level progress
        get().updateLevelProgress(levelId, {
          completed: true,
          bestGrade: grade,
          bestScore: newBestScore,
          bestTime: newBestTime,
          completedAt: now,
          attempts: (currentProgress?.attempts || 0) + 1,
        })

        // Update overall statistics if this is first completion
        if (!wasCompleted) {
          set(state => ({
            levelsCompleted: state.levelsCompleted + 1,
            completionPercentage: state.calculateCompletionPercentage([]), // Will be calculated with actual levels
          }))
        }

        // Update current level to next available level
        // This would be determined by level progression logic
        if (!wasCompleted) {
          // Simple progression - could be enhanced with branching logic
          const nextLevelMatch = levelId.match(/(\w+)-(\d+)/)
          if (nextLevelMatch) {
            const [, prefix, num] = nextLevelMatch
            const nextLevelId = `${prefix}-${String(parseInt(num) + 1).padStart(2, '0')}`
            set({ currentLevel: nextLevelId })
          }
        }
      },

      getLevelProgress: (levelId: string): LevelProgress | null => {
        return get().levelProgress.get(levelId) || null
      },

      isLevelUnlocked: (levelId: string, allLevels: GameLevel[]): boolean => {
        const state = get()

        // Tutorial levels are always unlocked
        if (levelId.startsWith('tutorial')) {
          return true
        }

        // Find the level and check prerequisites
        const level = allLevels.find(l => l.id === levelId)
        if (!level) {
          return false
        }

        // Check if previous levels in sequence are completed
        const levelOrder = level.order
        const prerequisiteLevels = allLevels
          .filter(l => l.order < levelOrder)
          .sort((a, b) => a.order - b.order)

        return prerequisiteLevels.every(prereqLevel => {
          const progress = state.levelProgress.get(prereqLevel.id)
          return progress?.completed || false
        })
      },

      // Achievement management actions
      updateAchievementProgress: (
        achievementId: string,
        progress: Partial<AchievementProgress>
      ) => {
        const state = get()
        const currentProgress = state.achievementProgress.get(
          achievementId
        ) || {
          achievementId,
          unlocked: false,
          progress: 0,
          contentViewed: false,
        }

        const updatedProgress = { ...currentProgress, ...progress }

        set(state => ({
          achievementProgress: new Map(state.achievementProgress).set(
            achievementId,
            updatedProgress
          ),
        }))
      },

      unlockAchievement: (achievementId: string) => {
        const state = get()
        const currentProgress = state.achievementProgress.get(achievementId)

        if (currentProgress?.unlocked) {
          return // Already unlocked
        }

        get().updateAchievementProgress(achievementId, {
          unlocked: true,
          progress: 1.0,
          unlockedAt: Date.now(),
        })

        // Update overall achievement count
        set(state => ({
          achievementsUnlocked: state.achievementsUnlocked + 1,
        }))
      },

      checkAchievements: (achievements: Achievement[]): string[] => {
        const state = get()
        const newlyUnlocked: string[] = []

        achievements.forEach(achievement => {
          const progress = state.achievementProgress.get(achievement.id)

          if (progress?.unlocked) {
            return // Already unlocked
          }

          // Check achievement requirements
          const meetsRequirements = achievement.requirements.every(
            requirement => {
              switch (requirement.type) {
                case 'complete-level':
                  const levelProgress = state.levelProgress.get(
                    requirement.target as string
                  )
                  return levelProgress?.completed || false

                case 'achieve-grade':
                  // Check if any level was completed with this grade or better
                  const gradeMap = { A: 5, B: 4, C: 3, D: 2, F: 1 }
                  const targetGrade =
                    gradeMap[requirement.target as keyof typeof gradeMap]
                  return Array.from(state.levelProgress.values()).some(
                    lp => lp.bestGrade && gradeMap[lp.bestGrade] >= targetGrade
                  )

                case 'complete-under-time':
                  return Array.from(state.levelProgress.values()).some(
                    lp =>
                      lp.bestTime &&
                      lp.bestTime <= (requirement.target as number)
                  )

                case 'achieve-metric':
                  // This would check specific game metrics
                  return (
                    state.statistics.bestSuccessRate >=
                    (requirement.target as number)
                  )

                default:
                  return false
              }
            }
          )

          if (meetsRequirements) {
            get().unlockAchievement(achievement.id)

            // Unlock associated content
            if (achievement.unlocksContent) {
              get().unlockContent(achievement.unlocksContent.contentId)
            }

            newlyUnlocked.push(achievement.id)
          }
        })

        return newlyUnlocked
      },

      markContentViewed: (achievementId: string) => {
        get().updateAchievementProgress(achievementId, {
          contentViewed: true,
        })
      },

      // Portfolio content management actions
      unlockContent: (contentId: string) => {
        const state = get()

        if (state.unlockedContent.includes(contentId)) {
          return // Already unlocked
        }

        set(state => ({
          unlockedContent: [...state.unlockedContent, contentId],
        }))
      },

      isContentUnlocked: (contentId: string): boolean => {
        return get().unlockedContent.includes(contentId)
      },

      // Statistics management actions
      updateStatistics: (stats: Partial<PlayerProgression['statistics']>) => {
        set(state => ({
          statistics: { ...state.statistics, ...stats },
        }))
      },

      incrementTilesPlaced: (tileType: TileType) => {
        set(state => ({
          statistics: {
            ...state.statistics,
            totalTilesPlaced: state.statistics.totalTilesPlaced + 1,
            favoriteTileType: tileType, // Simplified - would track most used
          },
        }))
      },

      incrementConnections: () => {
        set(state => ({
          statistics: {
            ...state.statistics,
            totalConnections: state.statistics.totalConnections + 1,
          },
        }))
      },

      updatePlaytime: (sessionTime: number) => {
        set(state => ({
          totalPlaytime: state.totalPlaytime + sessionTime,
        }))
      },

      // Preferences management actions
      updatePreferences: (
        preferences: Partial<PlayerProgression['preferences']>
      ) => {
        set(state => ({
          preferences: { ...state.preferences, ...preferences },
        }))
      },

      // Progress calculations
      calculateCompletionPercentage: (allLevels: GameLevel[]): number => {
        const state = get()

        if (allLevels.length === 0) {
          return 0
        }

        const completedCount = allLevels.filter(level => {
          const progress = state.levelProgress.get(level.id)
          return progress?.completed || false
        }).length

        return Math.round((completedCount / allLevels.length) * 100)
      },

      getCurrentLevelRecommendation: (
        allLevels: GameLevel[]
      ): string | null => {
        const state = get()

        // Find the first unlocked, uncompleted level
        const recommendedLevel = allLevels
          .sort((a, b) => a.order - b.order)
          .find(level => {
            const isUnlocked = state.isLevelUnlocked(level.id, allLevels)
            const progress = state.levelProgress.get(level.id)
            const isCompleted = progress?.completed || false

            return isUnlocked && !isCompleted
          })

        return recommendedLevel?.id || null
      },

      resetProgression: () => {
        set(defaultPlayerProgression)
      },
    }),
    {
      name: 'progression-store',
      // Persist all progression data
      partialize: state => ({
        currentLevel: state.currentLevel,
        levelsCompleted: state.levelsCompleted,
        achievementsUnlocked: state.achievementsUnlocked,
        completionPercentage: state.completionPercentage,
        totalPlaytime: state.totalPlaytime,
        levelProgress: state.levelProgress,
        achievementProgress: state.achievementProgress,
        unlockedContent: state.unlockedContent,
        preferences: state.preferences,
        statistics: state.statistics,
      }),
    }
  )
)
